import { ReactComponent as MenuIcon } from "assets/MenuIcon.svg";
import useTopElement from "backend/hooks/model/useTopElement";
import GenericToolbar from "components/toolbars/GenericToolbar";
import { t } from "i18next";
import useMeaQuery from "qpr-base/apihooks/useMeaQuery";
import { getKpisAndProjectsManagementHierarchyMode } from "qpr-base/configuration";
import { useAppContext } from "qpr-base/context/AppCtxProvider";
import { getElementName } from "qpr-base/tplutils/utils";
import Loader from "qpr-base/ui/components/Loader";
import PageContentMenu from "qpr-base/uicomponents/view/PageContentMenu";
import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import ManagementTable from "./ManagementTable";
import {
  mapKpisAndProjects,
  mapPerspectiveHierarchy,
  mapScorecardHierarchy,
} from "./dataMappers";
import { MappedData, RawData } from "./index.dto";
import SideMenu from "./sideMenu";
import { useStyles } from "./stylesKpiProjectManagement";
import ComponentThemes from "./themes";
import useKpisAndProjectsQueries from "./useKpisAndProjectsQueries";

const KPIsAndProjectsManagement = () => {
  const ctx = useAppContext();
  const modelId = `SC.${ctx.model}`;
  const classes = useStyles(ctx)();
  const [openCategory, setopenCategory] = useState<
    null | undefined | MappedData
  >(undefined);
  const [selectedElement, setSelectedElement] = useState<
    undefined | MappedData
  >(undefined);

  const [openSubCategory, setOpenSubCategory] = useState<
    null | undefined | MappedData
  >(undefined);
  const history = useHistory();

  const hierarchyMode = getKpisAndProjectsManagementHierarchyMode();

  const {
    hierarchyData,
    topElements,
    topElementsLoading,
    isAdmin,
    isHierarchyDataLoading,
    kpisConfigsData,
    kpisConfigsLoading,
    projectsConfigsData,
    projectsConfigsLoading,
    userRole,
  } = useKpisAndProjectsQueries(hierarchyMode);

  const { data: topElement, isLoading: topElementLoading } = useTopElement(
    "L0",
    'id,name,typename,value(series="JS_ATTR",period="firstforseries",as="JS_ATTR")'
  );

  const topScorecardSymbol = "L0";
  const {
    data: kpisAndProjectsData,
    isLoading: kpisAndProjectsLoading,
    refetch: refetchKPIAndProjectsData,
  } = useMeaQuery<Array<RawData>>(
    `[${modelId}].scorecard(criteria="symbol=\\"${topScorecardSymbol}\\"").childobjects(recursive=1,keepOriginals="1").topelement.childobjects(recursive=1,criteria="typename=\\"Initiative\\" OR typename=\\"KPI\\"")`,
    `id,symbol,name,typename,scorecard.topelementid(as="topelementid"),scorecard.symbol(as="scorecardsymbol"),scorecard.name(as="scorecardname"),parentelementid(as="parentid"),measure.parent.symbol(as="parentsymbol"),value(series="JS_ATTR", period="firstforseries",as="translations")`,
    "typename,scorecard.name,name",
    undefined,
    undefined,
    undefined,
    "accesslevel>0"
  );

  const [openSideMenu, setOpenSideMenu] = useState(true);
  const model = useAppContext((state) => state.model);
  if (
    isHierarchyDataLoading ||
    kpisAndProjectsLoading ||
    projectsConfigsLoading ||
    kpisConfigsLoading ||
    topElementLoading ||
    topElementsLoading
  ) {
    return <Loader />;
  }

  const hierarchy =
    hierarchyMode === "Scorecards"
      ? mapScorecardHierarchy(hierarchyData?.data)
      : mapPerspectiveHierarchy(hierarchyData?.data);
  const kpisAndProjects = mapKpisAndProjects(
    selectedElement,
    kpisAndProjectsData?.data,
    hierarchyMode
  );

  const linkHandler = (type: string) => {
    history.push(`/${ctx.tenant}/${model}/${type}`);
  };

  const elementDeleted = (id: string) => {
    refetchKPIAndProjectsData();
  };

  const getLevelIndex = (): number => {
    if (openSubCategory) return 2;
    if (openCategory) return 1;
    return 0;
  };

  const categoryPicker = (category: MappedData, subCategory?: MappedData) => {
    if (JSON.stringify(openCategory) === JSON.stringify(category)) {
      if (
        subCategory &&
        JSON.stringify(subCategory) === JSON.stringify(openSubCategory)
      ) {
        setOpenSubCategory(undefined);
        return;
      }
      if (!subCategory) {
        setopenCategory(undefined);
        return;
      }
      setOpenSubCategory(subCategory);
      return;
    }
    setopenCategory(category);
    setOpenSubCategory(undefined);
  };

  return (
    <>
      <GenericToolbar
        title={t("adminTools.KPIs and Projects Management")}
        icon={"/icons/navitems/KPIsAndProjectsManagement.svg"}
        hidePeriodSelector={true}
      />
      <div className={classes.pageContainer}>
        <SideMenu
          open={openSideMenu}
          setopenCategory={categoryPicker}
          setSelectedElement={setSelectedElement}
          openCategory={openCategory}
          hierarchy={hierarchy}
          topElements={topElements || []}
        />
        <div className={classes.pageContentWrapper}>
          <div className="drawerIcon">
            <MenuIcon onClick={() => setOpenSideMenu(!openSideMenu)} />
          </div>
          <ComponentThemes.PageContentMenu>
            <PageContentMenu
              componentData={{
                items: [
                  { title: t("toolbar.KPIs"), anchor: "kpis" },
                  { title: t("toolbar.Projects"), anchor: "projects" },
                ],
              }}
            />
          </ComponentThemes.PageContentMenu>
          <div className={classes.contentContainer}>
            <div className={classes.titleContainer}>
              <div className={classes.titleType}>
                {t(
                  `configtypes.${
                    openSubCategory?.typename ||
                    openCategory?.typename ||
                    "Scorecard"
                  }`
                )}
              </div>
              <h1 className={classes.title}>
                {openSubCategory
                  ? getElementName(
                      {
                        ...openSubCategory,
                        ...{ JS_ATTR: openSubCategory.translations },
                      },
                      ctx.language
                    )
                  : openCategory
                  ? getElementName(
                      {
                        ...openCategory,
                        ...{ JS_ATTR: openCategory.translations },
                      },
                      ctx.language
                    )
                  : getElementName(topElement, ctx.language)}{" "}
              </h1>
            </div>

            <ManagementTable
              linkHandler={linkHandler}
              elements={kpisAndProjects.kpis}
              topElements={topElements || []}
              topElement={topElement}
              elementDeleted={elementDeleted}
              selectedLevelIndex={getLevelIndex()}
              selectedLevel={
                openSubCategory?.typename ||
                openCategory?.typename ||
                "Scorecard"
              }
              selectedElement={selectedElement}
              configsData={
                kpisConfigsData?.data || {
                  create: false,
                  delete: false,
                  edit: false,
                }
              }
              isAdmin={isAdmin}
              isKpi
              userRole={userRole}
            />
            <ManagementTable
              linkHandler={linkHandler}
              elements={kpisAndProjects.projects}
              elementDeleted={elementDeleted}
              topElements={topElements || []}
              selectedLevelIndex={getLevelIndex()}
              selectedLevel={
                openSubCategory?.typename ||
                openCategory?.typename ||
                "Scorecard"
              }
              selectedElement={selectedElement}
              configsData={
                projectsConfigsData?.data || {
                  create: false,
                  delete: false,
                  edit: false,
                }
              }
              isAdmin={isAdmin}
              isKpi={false}
              userRole={userRole}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default KPIsAndProjectsManagement;
